import React, { useState } from "react";
import { Button, FormControl, TextField, Alert } from "@mui/material";
import { useAppDispatch } from "../../../redux/store";
import {
  forgotPasswordThunk,
  updatePasswordThunk,
} from "../../../redux/thunks/auth.thunk";
import Joi from "joi";
import { IWithClassName } from "../../../theme/default.theme";
import AuthFrame from "../../../component/auth-frame";
import { TabEnum } from "../../../component/auth-frame/component";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import MailOutlinedIcon from "@mui/icons-material/MailOutlined";
import { useParams } from "react-router-dom";

interface IError {
  email?: string;
  password?: string;
  confirmPassword?: string;
  otp?: string;
}

const ForgotPassword = ({ className }: IWithClassName) => {
  const { token } = useParams();
  const [showSuccessMessage, setShowSuccessMessage] = useState<{
    isError: boolean;
    message: string;
  }>();

  const schemaStep1 = Joi.object({
    email: Joi.string()
      .email({ tlds: { allow: false } })
      .required()
      .messages({
        "string.email": "Email must be a valid email",
        "string.empty": "Email is required",
      }),
  });

  const schemaStep2 = Joi.object({
    password: Joi.string().min(8).max(20).required().messages({
      "string.min": "Password must be at least 8 characters",
      "string.max": "Password must be at most 20 characters",
      "string.empty": "Password is required",
    }),
    confirmPassword: Joi.string()
      .valid(Joi.ref("password"))
      .required()
      .messages({
        "string.empty": "Confirm password is required",
        "any.only": "Confirm password must match password",
      }),
  });

  const [forgotPasswordFrom, setForgotPasswordForm] = useState({
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [error, setError] = useState<IError>({});

  const dispatch = useAppDispatch();

  const onClick = () => {
    setError({});

    schemaStep1
      .validateAsync({ email: forgotPasswordFrom.email })
      .then(async () =>
        setShowSuccessMessage(
          await dispatch(forgotPasswordThunk(forgotPasswordFrom.email)),
        ),
      )
      .catch((err) => {
        setError((state) => ({
          ...state,
          [err.details[0].path[0]]: err.details[0].message,
        }));
      });
  };

  const updatePassword = () => {
    schemaStep2
      .validateAsync({
        password: forgotPasswordFrom.password,
        confirmPassword: forgotPasswordFrom.confirmPassword,
      })
      .then(async () =>
        setShowSuccessMessage(
          await dispatch(
            updatePasswordThunk(forgotPasswordFrom.password, token ?? ""),
          ),
        ),
      )
      .catch((err) => {
        setError((state) => ({
          ...state,
          [err.details[0].path[0]]: err.details[0].message,
        }));
      });
  };

  return (
    <AuthFrame tab={TabEnum.FORGOT}>
      <FormControl className={className}>
        {showSuccessMessage && (
          <Alert
            severity={showSuccessMessage.isError ? "error" : "success"}
            className="success-cont"
          >
            {showSuccessMessage.message}
          </Alert>
        )}
        {token ? (
          <ResetPassword
            error={error}
            setForgotPasswordForm={setForgotPasswordForm}
            onClick={updatePassword}
          />
        ) : (
          <ForgotPasswordEmail
            error={error}
            setForgotPasswordForm={setForgotPasswordForm}
            onClick={onClick}
          />
        )}
      </FormControl>
    </AuthFrame>
  );
};

interface IForgotPasswordEmailProps {
  error: IError;
  setForgotPasswordForm: React.Dispatch<
    React.SetStateAction<{
      email: string;
      password: string;
      confirmPassword: string;
    }>
  >;
  onClick: () => void;
}

const ResetPassword = ({
  error,
  setForgotPasswordForm,
  onClick,
}: IForgotPasswordEmailProps) => (
  <>
    <TextField
      type="password"
      label="New Password"
      error={!!error.password}
      helperText={error.password}
      onChange={({ currentTarget }) =>
        setForgotPasswordForm((state) => ({
          ...state,
          password: currentTarget.value,
        }))
      }
      value={error.password}
      // InputProps={{
      //     endAdornment: <LockOutlinedIcon />,
      // }}
    />
    <TextField
      type="password"
      label="Confirm Password"
      error={!!error.confirmPassword}
      helperText={error.confirmPassword}
      onChange={({ currentTarget }) =>
        setForgotPasswordForm((state) => ({
          ...state,
          confirmPassword: currentTarget.value,
        }))
      }
      InputProps={
        {
          //endAdornment: <LockOutlinedIcon />,
        }
      }
    />
    <Button
      variant="contained"
      onClick={onClick}
      //startIcon={<LockOutlinedIcon />}
    >
      Update Password
    </Button>
  </>
);

const ForgotPasswordEmail = ({
  error,
  setForgotPasswordForm,
  onClick,
}: IForgotPasswordEmailProps) => (
  <>
    <TextField
      label="E-mail"
      error={!!error.email}
      helperText={error.email}
      onChange={({ currentTarget }) =>
        setForgotPasswordForm((state) => ({
          ...state,
          email: currentTarget.value,
        }))
      }
      InputProps={{
        endAdornment: <MailOutlinedIcon />,
      }}
    />
    <Button
      variant="contained"
      onClick={onClick}
      startIcon={<MarkEmailReadOutlinedIcon />}
    >
      Reset Password
    </Button>
  </>
);

export default ForgotPassword;
